<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useAccount } from "#/account/useAccount";
import { usePlural } from "#/utils/usePlural";
import FormInput from "#/form/FormInput.vue";

interface Props {
  readonly email: string;
  readonly isLoading?: boolean;
  readonly errorMessage?: string;
}

const emits = defineEmits<{
  "form:approve": [code: string];
  "form:code-resend": [];
  "error:update-message": [code: string];
  "error:clear-message": [];
  "change-email": [];
}>();

const props = defineProps<Props>();

const SECONDS_FOR_RESEND = 30;

const { resendEmailCode } = useAccount();
const { pluralizeSeconds } = usePlural();
const readOnlyEmail = ref(props.email);
const codeInput = ref();
const timerId = ref();
const time = ref(SECONDS_FOR_RESEND);

const approve = () => {
  emits("form:approve", codeInput.value);
};

const resendCode = async () => {
  try {
    codeInput.value = "";
    emits("error:clear-message");
    await resendEmailCode();
    time.value = SECONDS_FOR_RESEND;
    startTimer();
  } catch (e) {
    emits("error:update-message", e as string);
  }
};

const startTimer = () => {
  timerId.value = setInterval(() => {
    time.value--;

    if (time.value <= 1) {
      clearInterval(timerId.value);
      timerId.value = null;
    }
  }, 1000);
};

onMounted(() => startTimer());

onUnmounted(() => {
  clearInterval(timerId.value);
  timerId.value = null;
});
</script>

<template>
  <span class="color-dark flex mb-20 text-small justify-center">
    Отправили код на указанную почту, чтобы подтвердить аккаунт
  </span>

  <form-input
    v-model="readOnlyEmail"
    label="Новый e-mail"
    class="mb-20"
    disabled
  />

  <form-input
    v-model="codeInput"
    label="Код подтверждения"
    placeholder="Введите код"
    @keyup.enter="approve"
  />

  <div class="flex justify-between mb-20">
    <div>
      <span v-if="timerId" class="color-passive">
        Получить новый код ({{ pluralizeSeconds(time) }})
      </span>
      <a v-else href="javascript:void(0)" @click.stop="resendCode">
        Получить новый код
      </a>
    </div>

    <a class="cursor-pointer" @click="emits('change-email')">
      Изменить почту
    </a>
  </div>

  <div v-if="errorMessage" class="mb-20 color-error">
    {{ props.errorMessage }}
  </div>

  <button
    type="submit"
    class="w-full flex justify-center counter-form__submit btn"
    :class="props.isLoading ? 'tag-disable' : 'tag-primary'"
    :disabled="props.isLoading"
    @click="approve"
  >
    Подтвердить
  </button>
</template>
